import React from 'react';
import PropTypes from 'prop-types';

/**
 * LessonTextRenderer - Renders lesson text content with natural, readable formatting
 * Handles both HTML content (from WYSIWYG editor) and plain text with markdown
 * Keeps the look organic and textbook-like (non-AI appearance)
 */
const LessonTextRenderer = ({ content }) => {
  if (!content) return null;

  // Check if content contains HTML tags
  const isHTML = /<[^>]+>/.test(content);
  
  if (isHTML) {
    // Render HTML content directly (from WYSIWYG editor)
    return (
      <div 
        className="lesson-text-content w-full max-w-none px-2 sm:px-4"
        dangerouslySetInnerHTML={{ __html: content }}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          lineHeight: '1.7',
          color: '#374151'
        }}
      />
    );
  }

  // Otherwise, parse as plain text/markdown (legacy content)

  // Split content into lines for processing
  const lines = content.split('\n');
  const elements = [];
  let currentParagraph = [];
  let listItems = [];
  let isInList = false;

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      elements.push({
        type: 'paragraph',
        content: currentParagraph.join(' ').trim()
      });
      currentParagraph = [];
    }
  };

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push({
        type: 'list',
        items: [...listItems]
      });
      listItems = [];
      isInList = false;
    }
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    
    // Skip empty lines but treat as paragraph separator
    if (!trimmed) {
      flushList();
      flushParagraph();
      return;
    }

    // Detect markdown headings (## or #)
    const markdownHeadingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    
    // Detect numbered headings (1., 2., etc) or Chapter/Unit patterns
    const numberedHeadingMatch = trimmed.match(/^(\d+\.?\s+|Chapter\s+\d+|Unit\s+\d+|CHAPTER\s+\d+|UNIT\s+\d+)/i);
    
    // Detect ALL CAPS headings (at least 3 words in caps)
    const isAllCapsHeading = /^[A-Z][A-Z\s]{8,}$/.test(trimmed);
    
    // Detect title case headings (first letter of each word capitalized, no punctuation at end)
    const isTitleCaseHeading = /^[A-Z][a-z]+(\s+[A-Z][a-z]+)+$/.test(trimmed) && trimmed.length < 100;
    
    // Detect headings with special markers or all uppercase
    if (markdownHeadingMatch || isAllCapsHeading || isTitleCaseHeading || numberedHeadingMatch) {
      flushList();
      flushParagraph();
      
      // Determine heading level
      let level = 2; // default
      let headingContent = trimmed;
      
      if (markdownHeadingMatch) {
        level = markdownHeadingMatch[1].length; // # = 1, ## = 2, ### = 3, etc.
        headingContent = markdownHeadingMatch[2]; // Text after the #
      } else if (isAllCapsHeading || /^(CHAPTER|UNIT|PART|SECTION)/i.test(trimmed)) {
        level = 1; // Major heading
      } else if (numberedHeadingMatch) {
        level = 2; // Numbered section
      }
      
      elements.push({
        type: 'heading',
        level,
        content: headingContent
      });
      return;
    }

    // Detect bullet points or numbered lists
    const bulletMatch = trimmed.match(/^[•\-*>]\s+(.+)$/);
    const numberedMatch = trimmed.match(/^(\d+[.)]\s+|\(?[a-z][.)]\s+|\(?[ivxIVX]+[.)]\s+)(.+)$/);
    
    if (bulletMatch || numberedMatch) {
      flushParagraph();
      isInList = true;
      const content = bulletMatch ? bulletMatch[1] : numberedMatch[2];
      const marker = bulletMatch ? '•' : numberedMatch[1].trim();
      listItems.push({ marker, content });
      return;
    }

    // Regular paragraph text
    if (isInList) {
      flushList();
    }
    
    currentParagraph.push(trimmed);
  });

  // Flush any remaining content
  flushList();
  flushParagraph();

  // Function to parse inline formatting (bold, italic, underline, links)
  const parseInlineFormatting = (text) => {
    const parts = [];
    let currentIndex = 0;
    let key = 0;
    let plainTextBuffer = '';

    const flushPlainText = () => {
      if (plainTextBuffer) {
        parts.push(plainTextBuffer);
        plainTextBuffer = '';
      }
    };

    // Process the text character by character
    while (currentIndex < text.length) {
      let matched = false;

      // Check for bold (**text**)
      if (text.substr(currentIndex, 2) === '**') {
        const endIndex = text.indexOf('**', currentIndex + 2);
        if (endIndex !== -1 && endIndex > currentIndex + 2) {
          flushPlainText();
          const content = text.substring(currentIndex + 2, endIndex);
          parts.push(<strong key={key++}>{content}</strong>);
          currentIndex = endIndex + 2;
          matched = true;
        }
      }

      // Check for underline (__text__)
      if (!matched && text.substr(currentIndex, 2) === '__') {
        const endIndex = text.indexOf('__', currentIndex + 2);
        if (endIndex !== -1 && endIndex > currentIndex + 2) {
          flushPlainText();
          const content = text.substring(currentIndex + 2, endIndex);
          parts.push(<u key={key++}>{content}</u>);
          currentIndex = endIndex + 2;
          matched = true;
        }
      }

      // Check for italic (*text* but not **) - must check after bold
      if (!matched && text[currentIndex] === '*' && text[currentIndex + 1] !== '*') {
        const endIndex = text.indexOf('*', currentIndex + 1);
        if (endIndex !== -1 && text[endIndex + 1] !== '*' && endIndex > currentIndex + 1) {
          flushPlainText();
          const content = text.substring(currentIndex + 1, endIndex);
          parts.push(<em key={key++}>{content}</em>);
          currentIndex = endIndex + 1;
          matched = true;
        }
      }

      // Check for links [text](url)
      if (!matched && text[currentIndex] === '[') {
        const closeBracket = text.indexOf(']', currentIndex);
        if (closeBracket !== -1 && text[closeBracket + 1] === '(') {
          const closeParen = text.indexOf(')', closeBracket + 2);
          if (closeParen !== -1) {
            flushPlainText();
            const linkText = text.substring(currentIndex + 1, closeBracket);
            const url = text.substring(closeBracket + 2, closeParen);
            parts.push(
              <a 
                key={key++} 
                href={url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                {linkText}
              </a>
            );
            currentIndex = closeParen + 1;
            matched = true;
          }
        }
      }

      // If no formatting matched, add the current character to plain text buffer
      if (!matched) {
        plainTextBuffer += text[currentIndex];
        currentIndex++;
      }
    }

    // Flush any remaining plain text
    flushPlainText();

    return parts.length > 0 ? parts : text;
  };

  return (
    <div className="lesson-text-content w-full max-w-none px-2 sm:px-4">
      {elements.map((element, index) => {
        if (element.type === 'heading') {
          const HeadingTag = element.level === 1 ? 'h2' : 'h3';
          const className = element.level === 1
            ? 'text-xl sm:text-2xl font-bold text-gray-900 mb-6 mt-8 pb-3 border-b-2 border-gray-200 first:mt-0'
            : 'text-lg sm:text-xl font-semibold text-gray-800 mb-4 mt-6 first:mt-0';
          
          return (
            <HeadingTag key={index} className={className}>
              {parseInlineFormatting(element.content)}
            </HeadingTag>
          );
        }

        if (element.type === 'paragraph') {
          return (
            <p key={index} className="text-base sm:text-lg leading-relaxed text-gray-700 mb-4">
              {parseInlineFormatting(element.content)}
            </p>
          );
        }

        if (element.type === 'list') {
          return (
            <ul key={index} className="mb-6 space-y-2">
              {element.items.map((item, itemIndex) => (
                <li key={itemIndex} className="flex items-start gap-3 text-gray-700">
                  <span className="text-blue-600 font-medium mt-1 flex-shrink-0">
                    {item.marker}
                  </span>
                  <span className="leading-relaxed">{parseInlineFormatting(item.content)}</span>
                </li>
              ))}
            </ul>
          );
        }

        return null;
      })}

      <style>
        {`
        .lesson-text-content {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          line-height: 1.7;
          color: #374151;
        }
        
        /* HTML content styles (from WYSIWYG editor) */
        .lesson-text-content strong {
          font-weight: 600;
        }
        
        .lesson-text-content em {
          font-style: italic;
        }
        
        .lesson-text-content u {
          text-decoration: underline;
        }
        
        .lesson-text-content h2 {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 1.5rem 0 1rem 0;
          color: #1f2937;
        }
        
        .lesson-text-content ul,
        .lesson-text-content ol {
          margin: 1rem 0;
          padding-left: 2rem;
        }
        
        .lesson-text-content li {
          margin: 0.5rem 0;
        }
        
        .lesson-text-content a {
          color: #2563eb;
          text-decoration: underline;
        }
        
        .lesson-text-content a:hover {
          color: #1d4ed8;
        }
        
        /* Improve readability */
        .lesson-text-content p {
          margin: 1rem 0;
          text-align: justify;
          text-justify: inter-word;
          hyphens: auto;
          -webkit-hyphens: auto;
          -ms-hyphens: auto;
        }

        /* On small screens, prefer left-aligned text for better word-wrapping */
        @media (max-width: 640px) {
          .lesson-text-content p {
            text-align: left;
          }
        }
        
        /* Better spacing for nested content */
        .lesson-text-content > *:first-child {
          margin-top: 0 !important;
        }
        
        .lesson-text-content > *:last-child {
          margin-bottom: 0 !important;
        }
        `}
      </style>
    </div>
  );
};

LessonTextRenderer.propTypes = {
  content: PropTypes.string
};

export default LessonTextRenderer;
